import type {
  EpgInfoDto,
  EpgProgramme,
  HybridSearchPayload,
  TvEpgApiDto,
} from '@setplex/tria-api'

import { MediaTypes } from '@setplex/tria-api'
import { SECTION_ITEM_LIMIT } from './sections'

const WATCH_NOW_EPG_LIMIT = SECTION_ITEM_LIMIT.WATCH_NOW ?? 12

/**
 * Watch Now hits arrive as bare `TV_CHANNEL` items with no `programme`
 * (FP-4276). Programme cards need the on-air EPG row, which iOS already loads
 * in one `POST /media/tv/epg` batch. Cap the ids at the section's render limit
 * so we do not fetch programmes the grid will drop.
 */
export function collectWatchNowEpgIds(
  items: readonly HybridSearchPayload[]
): string[] {
  const epgIds: string[] = []
  const seen = new Set<string>()
  let watchNowCount = 0

  for (const item of items) {
    if (item.section !== 'WATCH_NOW') continue
    if (watchNowCount >= WATCH_NOW_EPG_LIMIT) continue
    watchNowCount += 1

    const epgId = watchNowChannelEpgId(item)
    if (epgId == null || seen.has(epgId)) continue

    seen.add(epgId)
    epgIds.push(epgId)
  }

  return epgIds
}

export function attachCurrentWatchNowProgrammes(
  items: readonly HybridSearchPayload[],
  epg: TvEpgApiDto,
  nowMs: number
): HybridSearchPayload[] {
  return items.map((item) => {
    const epgId = watchNowChannelEpgId(item)
    if (epgId == null) return item

    const programme = pickOnAirProgramme(epg[epgId], nowMs)
    return programme == null ? item : { ...item, programme }
  })
}

function watchNowChannelEpgId(item: HybridSearchPayload): string | undefined {
  if (
    item.section !== 'WATCH_NOW' ||
    item.contentType !== MediaTypes.TV_CHANNEL ||
    item.programme != null
  ) {
    return undefined
  }

  const epgId = 'epgId' in item ? item.epgId : undefined
  return epgId || undefined
}

function pickOnAirProgramme(
  programs: EpgInfoDto[] | undefined,
  nowMs: number
): EpgProgramme | undefined {
  if (programs == null || programs.length === 0) return undefined

  const airing = programs.find((program) => {
    if (!program.title) return false

    const startMs = new Date(program.start).getTime()
    const stopMs = new Date(program.stop).getTime()
    if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) return false

    return startMs <= nowMs && nowMs < stopMs
  })

  if (airing == null) return undefined

  return {
    programmeTitle: airing.title,
    startMs: new Date(airing.start).getTime(),
    stopMs: new Date(airing.stop).getTime(),
  }
}
